/**
 * Reserva por email.
 *
 * El sitio es estático: esto es lo único que habla con el servidor. Del otro
 * lado hay un solo PHP (`/api/reserva.php`) que manda el aviso al anfitrión y
 * el acuse de recibo al huésped por SMTP.
 */

export const RESERVATION_ENDPOINT = "/api/reserva.php";

/** Errores con `code` para poder resaltar el campo que falló. */
export class ReservationError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "ReservationError";
    this.code = code;
  }
}

const fallbackMessage = (lang) =>
  lang === "en"
    ? "We could not send your request. Please try again or reach us on WhatsApp."
    : "No pudimos enviar tu pedido. Probá de nuevo o escribinos por WhatsApp.";

/**
 * @returns {Promise<{ ref: string, guestEmailed: boolean }>}
 * @throws  {ReservationError}
 */
export async function submitReservation(payload, { lang = "es", signal } = {}) {
  let res;
  try {
    res = await fetch(RESERVATION_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new ReservationError(
      lang === "en"
        ? "We could not reach the server. Check your connection or reach us on WhatsApp."
        : "No pudimos conectarnos al servidor. Revisá tu conexión o escribinos por WhatsApp.",
      "network"
    );
  }

  // El servidor puede devolver HTML (una página de error de nginx o del WAF),
  // así que no se puede asumir JSON ni siquiera con status 200.
  let data = null;
  try {
    data = await res.json();
  } catch {
    throw new ReservationError(fallbackMessage(lang), "unreadable");
  }

  if (!res.ok || !data?.ok) {
    throw new ReservationError(data?.error || fallbackMessage(lang), data?.code || "unknown");
  }

  return { ref: data.ref || "", guestEmailed: data.guest_emailed !== false };
}
